/**
 * Mobile optimization utilities for Curbside Quest
 */

// Detect if user is on a mobile device
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || window.innerWidth <= 768;
};

// Detect if user is on iOS
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

// Detect if user is on Android
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /Android/.test(navigator.userAgent);
};

// Get safe area insets for devices with notches
export const getSafeAreaInsets = () => {
  if (typeof window === 'undefined') return { top: 0, bottom: 0, left: 0, right: 0 };
  
  const style = getComputedStyle(document.documentElement);
  
  return {
    top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0'),
    bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0'),
    left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0'),
    right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0'),
  };
};

// Optimize touch events for better performance
export const optimizeTouchEvents = (element: HTMLElement) => {
  // Add passive event listeners for better scroll performance
  element.addEventListener('touchstart', () => {}, { passive: true });
  element.addEventListener('touchmove', () => {}, { passive: true });
  element.addEventListener('touchend', () => {}, { passive: true });
};

// Prevent zoom on input focus (iOS)
export const preventInputZoom = () => {
  if (!isIOS()) return;
  
  const inputs = document.querySelectorAll('input, select, textarea');
  inputs.forEach((input) => {
    const element = input as HTMLElement;
    if (element.style.fontSize === '' || parseInt(element.style.fontSize) < 16) {
      element.style.fontSize = '16px';
    }
  });
};

// Handle viewport height changes (mobile browsers)
export const handleViewportHeight = () => {
  const setVH = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };
  
  setVH();
  window.addEventListener('resize', setVH);
  window.addEventListener('orientationchange', setVH);
  
  return () => {
    window.removeEventListener('resize', setVH);
    window.removeEventListener('orientationchange', setVH);
  };
};

// Optimize images for mobile
export const optimizeImageForMobile = (src: string, width?: number): string => {
  if (!src) return src;
  
  // If it's a Firebase Storage URL, add size parameters
  if (src.includes('firebasestorage.googleapis.com')) {
    const url = new URL(src);
    if (width) {
      url.searchParams.set('w', width.toString());
    }
    // Add mobile-optimized quality
    url.searchParams.set('q', '80');
    return url.toString();
  }
  
  return src;
};

// Debounce function for performance optimization
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function for scroll events
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Check if device supports touch
export const supportsTouchEvents = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Get optimal image dimensions for mobile
export const getMobileImageDimensions = (containerWidth: number) => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const optimalWidth = Math.min(containerWidth * devicePixelRatio, 800); // Cap at 800px
  
  return {
    width: optimalWidth,
    height: Math.round(optimalWidth * 0.75), // 4:3 aspect ratio
  };
};

// Optimize scroll performance
export const optimizeScrollPerformance = () => {
  // Add will-change property to scrollable elements
  const scrollableElements = document.querySelectorAll('[data-scrollable]');
  scrollableElements.forEach((element) => {
    (element as HTMLElement).style.willChange = 'scroll-position';
  });
  
  // Use passive listeners for scroll events
  document.addEventListener('scroll', () => {}, { passive: true });
  document.addEventListener('wheel', () => {}, { passive: true });
  document.addEventListener('touchmove', () => {}, { passive: true });
};

// Handle orientation changes
export const handleOrientationChange = (callback: () => void) => {
  const handleChange = () => {
    // Small delay to ensure viewport has updated
    setTimeout(callback, 100);
  };
  
  window.addEventListener('orientationchange', handleChange);
  
  return () => {
    window.removeEventListener('orientationchange', handleChange);
  };
};

// Preload critical resources
export const preloadCriticalResources = (resources: string[]) => {
  resources.forEach((resource) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    
    if (resource.endsWith('.css')) {
      link.as = 'style';
    } else if (resource.match(/\.(jpg|jpeg|png|webp|svg)$/)) {
      link.as = 'image';
    } else if (resource.match(/\.(woff|woff2|ttf|otf)$/)) {
      link.as = 'font';
      link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  });
};

// Initialize mobile optimizations
export const initializeMobileOptimizations = () => {
  if (typeof window === 'undefined') return;
  
  // Handle viewport height
  const cleanupVH = handleViewportHeight();
  
  // Prevent input zoom on iOS
  preventInputZoom();
  
  // Optimize scroll performance
  optimizeScrollPerformance();
  
  // Return cleanup function
  return () => {
    cleanupVH();
  };
};